/**
 * @description Shared payment error banner for all payment flows.
 * Renders structured error data from ElavonErrorMapper consistently
 * across MerchantDashboard, PaymentRecorder, and customer portal.
 *
 * Key features:
 * - Visa Decline Management category-aware styling and messaging
 * - Retry guidance based on canRetry / isUserRecoverable flags
 * - Gateway configuration error handling (503 / GATEWAY_NOT_CONFIGURED)
 * - 3DS verification flow support
 * - Matches platform error pattern (FieldError, GeneralErrors)
 *
 * @dependencies
 * - None (pure presentational component)
 *
 * @notes
 * - Accepts either a structured error object (from gateway API response)
 *   or a plain string for backward compatibility
 * - The `error` prop shape from gateway: { error, code, canRetry, isUserRecoverable, category }
 * - For non-gateway errors, pass a string or { message: '...' }
 */

import React from 'react';

/**
 * Normalize error input into a consistent shape.
 * Handles: string, axios error response, structured gateway error object.
 */
function normalizeError(error) {
  // Plain string
  if (typeof error === 'string') {
    return {
      message: error,
      code: null,
      canRetry: false,
      isUserRecoverable: false,
      isGatewayConfig: false
    };
  }

  // Array of strings (existing PaymentRecorder pattern)
  if (Array.isArray(error)) {
    return {
      message: error.filter(Boolean).join(' '),
      messages: error.filter(Boolean),
      code: null,
      canRetry: false,
      isUserRecoverable: false,
      isGatewayConfig: false
    };
  }

  // Axios error (error.response.data contains gateway error)
  const data = error?.response?.data || error;

  const code = data.code || null;
  const isGatewayConfig = code === 'GATEWAY_NOT_CONFIGURED' ||
    error?.response?.status === 503 ||
    (typeof data.error === 'string' && data.error.includes('not configured'));

  return {
    message: data.error || data.message || data.userMessage || 'Payment failed. Please try again.',
    code,
    canRetry: data.canRetry ?? false,
    isUserRecoverable: data.isUserRecoverable ?? false,
    isGatewayConfig,
    chargeVoided: data.chargeVoided || false,
    voidFailed: data.voidFailed || false
  };
}

// --- Icon Components (matching platform SVG pattern from FieldError/GeneralErrors) ---

function WarningIcon({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

function XCircleIcon({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
    </svg>
  );
}

function RefreshIcon({ className = 'w-3.5 h-3.5' }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}

function CloseIcon({ className = 'w-5 h-5' }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function ShieldIcon({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

// --- Banner Variants ---

function GatewayConfigBanner({ onDismiss }) {
  return (
    <div className="p-4 bg-orange-50 border border-orange-200 rounded-2xl" role="alert" aria-live="polite">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <WarningIcon className="w-4 h-4 text-orange-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-orange-800">Payment Gateway Not Configured</h3>
          <p className="mt-1 text-sm text-orange-700">
            Please contact your administrator to set up payment processing.
          </p>
        </div>
        {onDismiss && (
          <button onClick={onDismiss} className="p-1 text-orange-400 hover:text-orange-600 hover:bg-orange-100 rounded-lg transition-colors">
            <CloseIcon />
          </button>
        )}
      </div>
    </div>
  );
}

function PermanentDeclineBanner({ message, code, onDismiss }) {
  return (
    <div className="p-4 bg-red-50 border border-red-200 rounded-2xl" role="alert" aria-live="polite">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <XCircleIcon className="w-4 h-4 text-red-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-red-800">{message}</h3>
          <p className="mt-1 text-sm text-red-700">Please use a different payment method.</p>
          {process.env.NODE_ENV === 'development' && code && (
            <p className="mt-1 text-xs text-red-500 font-mono">[{code}]</p>
          )}
        </div>
        {onDismiss && (
          <button onClick={onDismiss} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors">
            <CloseIcon />
          </button>
        )}
      </div>
    </div>
  );
}

function RecoverableDeclineBanner({ message, code, onDismiss, onRetry }) {
  return (
    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-2xl" role="alert" aria-live="polite">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <WarningIcon className="w-4 h-4 text-yellow-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-yellow-800">{message}</h3>
          {onRetry && (
            <button
              onClick={onRetry}
              className="inline-flex items-center gap-1.5 mt-2 text-sm font-medium text-yellow-700 hover:text-yellow-900"
            >
              <RefreshIcon />
              Try again
            </button>
          )}
          {process.env.NODE_ENV === 'development' && code && (
            <p className="mt-1 text-xs text-yellow-600 font-mono">[{code}]</p>
          )}
        </div>
        {onDismiss && (
          <button onClick={onDismiss} className="p-1 text-yellow-400 hover:text-yellow-600 hover:bg-yellow-100 rounded-lg transition-colors">
            <CloseIcon />
          </button>
        )}
      </div>
    </div>
  );
}

function ThreeDsBanner({ message, onDismiss, onRetry }) {
  return (
    <div className="p-4 bg-blue-50 border border-blue-200 rounded-2xl" role="alert" aria-live="polite">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <ShieldIcon className="w-4 h-4 text-blue-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-blue-800">Additional Verification Required</h3>
          <p className="mt-1 text-sm text-blue-700">{message}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="inline-flex items-center gap-1.5 mt-2 text-sm font-medium text-blue-700 hover:text-blue-900"
            >
              Continue Verification
            </button>
          )}
        </div>
        {onDismiss && (
          <button onClick={onDismiss} className="p-1 text-blue-400 hover:text-blue-600 hover:bg-blue-100 rounded-lg transition-colors">
            <CloseIcon />
          </button>
        )}
      </div>
    </div>
  );
}

function GenericDeclineBanner({ message, messages, code, canRetry, onDismiss, onRetry }) {
  return (
    <div className="p-4 bg-red-50 border border-red-200 rounded-2xl" role="alert" aria-live="polite">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <WarningIcon className="w-4 h-4 text-red-600" />
        </div>
        <div className="flex-1">
          {messages && messages.length > 1 ? (
            <>
              <h3 className="text-sm font-semibold text-red-800">Please fix the following:</h3>
              <ul className="mt-2 space-y-1">
                {messages.map((msg, idx) => (
                  <li key={idx} className="text-sm text-red-700 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0" />
                    {msg}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <h3 className="text-sm font-semibold text-red-800">
                {messages ? messages[0] : message}
              </h3>
            </>
          )}
          {canRetry && onRetry && (
            <button
              onClick={onRetry}
              className="inline-flex items-center gap-1.5 mt-2 text-sm font-medium text-red-700 hover:text-red-900"
            >
              <RefreshIcon />
              Try again
            </button>
          )}
          {process.env.NODE_ENV === 'development' && code && (
            <p className="mt-1 text-xs text-red-500 font-mono">[{code}]</p>
          )}
        </div>
        {onDismiss && (
          <button onClick={onDismiss} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors">
            <CloseIcon />
          </button>
        )}
      </div>
    </div>
  );
}

function ChargeVoidedBanner({ onDismiss, onRetry }) {
  return (
    <div className="p-4 bg-green-50 border border-green-200 rounded-2xl" role="alert" aria-live="polite">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <ShieldIcon className="w-4 h-4 text-green-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-green-800">Payment could not be completed</h3>
          <p className="mt-1 text-sm text-green-700">
            You have not been charged. The transaction was automatically reversed.
          </p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="inline-flex items-center gap-1.5 mt-2 text-sm font-medium text-green-700 hover:text-green-900"
            >
              <RefreshIcon />
              Try again
            </button>
          )}
        </div>
        {onDismiss && (
          <button onClick={onDismiss} className="p-1 text-green-400 hover:text-green-600 hover:bg-green-100 rounded-lg transition-colors">
            <CloseIcon />
          </button>
        )}
      </div>
    </div>
  );
}

function VoidFailedBanner({ onDismiss }) {
  return (
    <div className="p-4 bg-red-50 border border-red-200 rounded-2xl" role="alert" aria-live="polite">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <XCircleIcon className="w-4 h-4 text-red-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-red-800">Payment processing error</h3>
          <p className="mt-1 text-sm text-red-700">
            We're confirming your payment status. Please do not retry. Contact support if this charge appears on your statement.
          </p>
        </div>
        {onDismiss && (
          <button onClick={onDismiss} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors">
            <CloseIcon />
          </button>
        )}
      </div>
    </div>
  );
}

// --- Main Component ---

function PaymentErrorBanner({ error, onDismiss, onRetry, className = '' }) {
  if (!error) return null;

  const errorData = normalizeError(error);
  const { message, messages, code, canRetry, isUserRecoverable, isGatewayConfig, chargeVoided, voidFailed } = errorData;

  return (
    <div className={className}>
      {voidFailed ? (
        <VoidFailedBanner onDismiss={onDismiss} />
      ) : chargeVoided ? (
        <ChargeVoidedBanner onDismiss={onDismiss} onRetry={onRetry} />
      ) : isGatewayConfig ? (
        <GatewayConfigBanner onDismiss={onDismiss} />
      ) : code === 'THREE_DS_REQUIRED' || code === 'THREE_DS_FAILED' || code === 'THREE_DS_CHALLENGE' ? (
        <ThreeDsBanner message={message} onDismiss={onDismiss} onRetry={onRetry} />
      ) : canRetry === false && !isUserRecoverable ? (
        <PermanentDeclineBanner message={message} code={code} onDismiss={onDismiss} />
      ) : isUserRecoverable ? (
        <RecoverableDeclineBanner message={message} code={code} onDismiss={onDismiss} onRetry={onRetry} />
      ) : (
        <GenericDeclineBanner
          message={message}
          messages={messages}
          code={code}
          canRetry={canRetry}
          onDismiss={onDismiss}
          onRetry={onRetry}
        />
      )}
    </div>
  );
}

export default PaymentErrorBanner;
